import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  CheckIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'
import { RootState, AppDispatch } from '../../store'
import { 
  getSessionStatus, 
  setCurrentQuestion, 
  updateResponse, 
  submitSession,
  markQuestionVisited,
  selectCurrentQuestion,
  saveQuestionResponse,
  submitAssessment,
  createSession
} from '../../store/slices/testSlice'
import { startTimer, resetTimer, updateTimer } from '../../store/slices/timerSlice'
import Timer from '../../components/Timer'
import CodeEditor from '../../components/CodeEditor'
import { useWebSocket } from '../../services/websocket'
import { testAPI, assessmentAPI } from '../../services/api'
import { Question } from '../../store/slices/testSlice'
// testCompletionService removed - MySQL is the source of truth for assessment completion
import { useBackNavigationPrevention } from '../../hooks/useBackNavigationPrevention'
import clsx from 'clsx'
import toast from 'react-hot-toast'

const TestPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  
  const { 
    currentSession, 
    questions, 
    responses, 
    currentQuestionIndex, 
    isSubmitting 
  } = useSelector((state: RootState) => state.test)
  
  const { user } = useSelector((state: RootState) => state.auth)
  const { remainingTimeSeconds, totalDurationMinutes, isRunning } = useSelector((state: RootState) => state.timer)
  const currentQuestion = useSelector(selectCurrentQuestion)
  
  const [selectedLanguage, setSelectedLanguage] = useState('python')
  const [isInitialized, setIsInitialized] = useState(false)
  const [leftPanelWidth, setLeftPanelWidth] = useState(50) // Percentage
  const [isResizing, setIsResizing] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [submittedCodingQuestions, setSubmittedCodingQuestions] = useState<Set<number>>(new Set())
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout>()
  const timerInitializedRef = useRef(false)
  const autoSubmitTriggeredRef = useRef(false)
  const { connect, disconnect, isConnected } = useWebSocket()

  // Resolve driveId from session or encoded in sessionId (e.g. "assessment_9_...")
  const resolveDriveId = (): number | null => {
    if (currentSession?.drive_id) {
      const n = Number(currentSession.drive_id)
      return isNaN(n) ? null : n
    }
    if (sessionId) {
      const match = sessionId.match(/assessment_(\d+)/)
      if (match && match[1]) {
        const n = Number(match[1])
        return isNaN(n) ? null : n
      }
    }
    return null
  }
  
  // Back navigation prevention hook
  const { WarningModalComponent } = useBackNavigationPrevention(sessionId)
  
  // Assessment sequence navigation prevention
  const [showSequenceWarning, setShowSequenceWarning] = useState(false)
  
  useEffect(() => {
    const assessmentSequenceStarted = sessionStorage.getItem('assessmentSequenceStarted') === 'true'
    const currentDriveAssessmentSequence = sessionStorage.getItem('currentDriveAssessmentSequence')
    
    if (!assessmentSequenceStarted || !currentDriveAssessmentSequence) {
      return
    }

    const handlePopState = (event: PopStateEvent) => {
      if (location.pathname.includes('/test/')) {
        event.preventDefault()
        setShowSequenceWarning(true)
        window.history.pushState(null, '', location.pathname)
      }
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = 'Navigation Back from test is not allowed, complete all Assessment in one go. Sure want to leave the test?'
      return event.returnValue
    }

    window.history.pushState(null, '', location.pathname)
    window.addEventListener('popstate', handlePopState)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  // Initialize session and WebSocket
  useEffect(() => {
    if (!sessionId || isInitialized) return

    const initializeTest = async () => {
      try {
        // Get session status from backend (source of truth)
        const sessionData = await dispatch(getSessionStatus(sessionId)).unwrap()
        if (['submitted', 'completed', 'expired', 'cancelled'].includes(sessionData?.status)) {
          navigate(`/test/${sessionId}/completed`, { replace: true })
          return
        }
        
        // Connect WebSocket for real-time updates
        await connect(sessionId)
        
        console.log('Session data from backend:', { 
          status: sessionData?.status, 
          remaining_time: sessionData?.remaining_time_seconds,
          duration_minutes: sessionData?.duration_minutes
        })
        
        // Always sync timer with backend time (backend is source of truth)
        if (sessionData?.status === 'started' && sessionData.remaining_time_seconds) {
          console.log('Syncing timer with backend:', sessionData.remaining_time_seconds, 'seconds')
          dispatch(updateTimer({ 
            remainingTimeSeconds: sessionData.remaining_time_seconds,
            lastSyncTime: new Date().toISOString()
          }))
          
          // Start the timer if not already running
          if (!isRunning) {
            dispatch(startTimer({ 
              remainingTimeSeconds: sessionData.remaining_time_seconds,
              totalDurationMinutes: sessionData.duration_minutes
            }))
          }
        } else if (sessionData?.remaining_time_seconds) {
          console.log('Session not started yet, preparing timer with:', sessionData.remaining_time_seconds, 'seconds')
          dispatch(updateTimer({ 
            remainingTimeSeconds: sessionData.remaining_time_seconds,
            lastSyncTime: new Date().toISOString()
          }))
        }
        
        setIsInitialized(true)
      } catch (error) {
        console.error('Failed to initialize test:', error)
        toast.error('Failed to load test session')
        navigate('/dashboard')
      }
    }

    initializeTest()

    return () => {
      disconnect()
    }
  }, [sessionId, dispatch, navigate])

  // Sync timer with backend every 30 seconds and on window focus
  useEffect(() => {
    if (!sessionId) return

    const syncWithBackend = async () => {
      try {
        console.log('Syncing timer with backend...')
        const response = await testAPI.syncTimer(sessionId)
        const serverTime = response.data.remaining_time_seconds
        
        console.log(`Backend time: ${serverTime}s, Local time: ${remainingTimeSeconds}s, Total duration: ${currentSession?.duration_minutes}m`)
        
        // Validate server time before updating
        const maxAllowedTime = (currentSession?.duration_minutes || 0) * 60 + 10
        if (serverTime > maxAllowedTime) {
          console.error(`Server returned invalid time: ${serverTime}s exceeds max ${maxAllowedTime}s`)
          return
        }
        
        // Only update if there's a significant difference (more than 5 seconds)
        const timeDiff = Math.abs(serverTime - remainingTimeSeconds)
        if (timeDiff > 5) {
          console.log(`Significant time difference (${timeDiff}s), updating timer to match backend: ${serverTime}s`)
          dispatch(updateTimer({ 
            remainingTimeSeconds: serverTime,
            lastSyncTime: new Date().toISOString()
          }))
        } else {
          console.log(`Time difference is small (${timeDiff}s), skipping update`)
        }
      } catch (error: any) {
        console.error('Failed to sync timer with backend:', error)
        // If session not found, reload the session status
        if (error?.response?.status === 404) {
          console.log('Session not found during sync, reloading session status...')
          try {
            const sessionResult = await dispatch(getSessionStatus(sessionId))
            const sessionData = sessionResult.payload
            if (sessionData?.remaining_time_seconds) {
              dispatch(updateTimer({ 
                remainingTimeSeconds: sessionData.remaining_time_seconds,
                lastSyncTime: new Date().toISOString()
              }))
            }
          } catch (reloadError) {
            console.error('Failed to reload session status:', reloadError)
            toast.error('Session sync failed. Please refresh the page.')
          }
        }
      }
    }

    // Sync on window focus (when user returns to tab)
    const handleFocus = () => {
      console.log('Window focused, syncing timer...')
      syncWithBackend()
    }

    // Sync every 60 seconds (reduced frequency to prevent constant resets)
    const syncInterval = setInterval(syncWithBackend, 60000)
    
    // Add focus listener
    window.addEventListener('focus', handleFocus)

    return () => {
      clearInterval(syncInterval)
      window.removeEventListener('focus', handleFocus)
    }
  }, [sessionId, remainingTimeSeconds, dispatch])

  // Cleanup timer state when component unmounts
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current)
      // Reset timer when leaving test page (but not when going to completed page)
      const currentPath = window.location.pathname
      if (!currentPath.includes('/test/') || currentPath.includes('/completed')) {
        console.log('Leaving test page, resetting timer')
        dispatch(resetTimer())
      }
    }
  }, [dispatch])

  // Mark current question as visited
  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex >= 0) {
      const currentQuestion = questions[currentQuestionIndex]
      if (currentQuestion) {
        dispatch(markQuestionVisited(currentQuestion.id))
      }
    }
  }, [currentQuestionIndex, questions, dispatch])

  // Auto-save responses
  const handleAutoSave = (questionId: number, responseData: any) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      dispatch(updateResponse({ questionId, responseData }))
      // Save to server
      if (sessionId) {
        testAPI.saveResponse(sessionId, questionId, responseData)
          .then(() => console.log(`Saved response for question ${questionId}`))
          .catch(err => console.error(`Failed to save response for question ${questionId}:`, err))
      }
    }, 2000) // Save after 2 seconds of inactivity
  }

  const currentResponse = currentQuestion ? responses[currentQuestion.id] : null

  const handleQuestionChange = (index: number) => {
    if (index >= 0 && index < questions.length) {
      dispatch(setCurrentQuestion(index))
    }
  }

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      handleQuestionChange(currentQuestionIndex - 1)
    }
  }

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      handleQuestionChange(currentQuestionIndex + 1)
    }
  }

  const handleSubmitTest = async () => {
    if (!sessionId || !currentSession || !user) return

    const unansweredQuestions = questions.filter(q => !responses[q.id]?.response_data)
    
    if (unansweredQuestions.length > 0) {
      const proceed = window.confirm(
        `You have ${unansweredQuestions.length} unanswered questions. Are you sure you want to submit?`
      )
      if (!proceed) return
    }

    try {
      // Submit using new assessment responses system (only if we have a valid drive_id)
      const candidateId = user.id
      const driveId = resolveDriveId()
      const assessmentId = `${driveId ?? ''}_${currentSession.assessment_type}`

      if (driveId !== null) {
        // CRITICAL: Save all coding question responses that haven't been submitted yet
        // Candidates can submit code per question using "Submit Code" button
        // But if they haven't, we'll save them now before submitting the assessment
        const codingQuestions = questions.filter(q => q.question_type === 'coding')
        const savePromises = []
        
        for (const question of codingQuestions) {
          // Skip if already submitted via "Submit Code" button
          if (submittedCodingQuestions.has(question.id)) {
            console.log(`[handleSubmitTest] Question ${question.id} already submitted, skipping`)
            continue
          }

          const response = responses[question.id]
          // Get source code from response or current question state
          const sourceCode = response?.response_data?.source_code || 
                            (currentQuestion?.id === question.id ? 
                              (responses[question.id]?.response_data?.source_code || '') : '')
          
          if (sourceCode && sourceCode.trim()) {
            // Save coding response using submit-coding endpoint
            // This endpoint will run code against hidden test cases and save results
            try {
              const savePromise = assessmentAPI.submitCodingSolution(assessmentId, {
                candidate_id: candidateId,
                drive_id: driveId,
                question_id: String(question.id),
                source_code: sourceCode,
                language: response?.response_data?.language || selectedLanguage || 'python'
              }).catch(error => {
                console.error(`[handleSubmitTest] Failed to save coding response for question ${question.id}:`, error)
                // Don't throw - continue with other questions
                return null
              })
              savePromises.push(savePromise)
              console.log(`[handleSubmitTest] Saving coding response for question ${question.id} with ${sourceCode.length} chars`)
            } catch (error) {
              console.error(`[handleSubmitTest] Error preparing save for question ${question.id}:`, error)
            }
          } else {
            console.warn(`[handleSubmitTest] No source code found for coding question ${question.id}, skipping save`)
          }
        }
        
        // Wait for all coding responses to be saved
        if (savePromises.length > 0) {
          console.log(`[handleSubmitTest] Saving ${savePromises.length} coding responses before submission...`)
          const saveResults = await Promise.allSettled(savePromises)
          const successful = saveResults.filter(r => r.status === 'fulfilled').length
          const failed = saveResults.filter(r => r.status === 'rejected').length
          console.log(`[handleSubmitTest] Coding responses saved: ${successful} successful, ${failed} failed`)
        } else {
          const alreadySubmitted = codingQuestions.filter(q => submittedCodingQuestions.has(q.id)).length
          console.log(`[handleSubmitTest] All coding responses already submitted (${alreadySubmitted}/${codingQuestions.length}) or no code found`)
        }
        
        // Now submit the assessment (which will calculate results from saved responses)
        // CRITICAL: Capture timer values BEFORE any timer stops/resets
        // Timer counts down: remainingTimeSeconds decreases (e.g., 1800 -> 1500 -> 1200...)
        // time_taken = totalDurationMinutes - (remainingTimeSeconds / 60)
        
        // Get timer values from Redux state (most accurate)
        let finalRemainingSeconds = remainingTimeSeconds ?? 0
        let finalTotalDuration = totalDurationMinutes ?? (currentSession.duration_minutes ?? 0)
        
        // Fallback: If timer state is 0 or invalid, try to get from session
        if (finalRemainingSeconds === 0 && currentSession?.remaining_time_seconds) {
          finalRemainingSeconds = currentSession.remaining_time_seconds
          console.log('[handleSubmitTest] Using remaining_time_seconds from session:', finalRemainingSeconds)
        }
        
        // Ensure we have valid values
        if (finalRemainingSeconds <= 0 || finalTotalDuration <= 0) {
          console.warn('[handleSubmitTest] Invalid timer values:', {
            remainingTimeSeconds: finalRemainingSeconds,
            totalDurationMinutes: finalTotalDuration,
            fromRedux: { remainingTimeSeconds, totalDurationMinutes },
            fromSession: { 
              remaining_time_seconds: currentSession?.remaining_time_seconds,
              duration_minutes: currentSession?.duration_minutes 
            }
          })
        }
        
        const calculatedTimeTaken = finalTotalDuration > 0 && finalRemainingSeconds >= 0 
          ? (finalTotalDuration - (finalRemainingSeconds / 60)).toFixed(2) 
          : 0
        
        console.log('[handleSubmitTest] Submitting with timer data:', {
          remainingTimeSeconds: finalRemainingSeconds,
          totalDurationMinutes: finalTotalDuration,
          calculatedTimeTaken: calculatedTimeTaken + ' minutes',
          calculation: `${finalTotalDuration} - (${finalRemainingSeconds} / 60) = ${calculatedTimeTaken}`
        })
        
        await dispatch(submitAssessment({
          assessmentId,
          candidateId,
          driveId,
          remainingTimeSeconds: finalRemainingSeconds,
          totalDurationMinutes: finalTotalDuration
        })).unwrap()
      } else {
        console.warn('Skipping submitAssessment because drive_id could not be resolved from session/sessionId')
      }

      // Always submit using legacy session system
      await dispatch(submitSession(sessionId)).unwrap()
      
      // Mark assessment as completed
      if (user?.email) {
        console.log('Marking assessment as completed with data:', {
          assessmentId: currentSession.assessment_id,
          assessmentTitle: currentSession.assessment_title,
          assessmentType: currentSession.assessment_type,
          driveId: currentSession.drive_id,
          driveTitle: currentSession.drive_title,
          sessionId: sessionId,
          candidateEmail: user.email,
          durationMinutes: currentSession.duration_minutes
        })
        
        // Completion status is tracked in MySQL via assessment_responses table
        // No need for localStorage - MySQL is the source of truth
        console.log('Assessment completed - tracked in MySQL')
      }
      
      // Clear timer state when test is submitted
      dispatch(resetTimer())
      timerInitializedRef.current = false
      
      // Check if there are more assessments in the drive sequence
      const assessmentSequenceStr = sessionStorage.getItem('currentDriveAssessmentSequence')
      const currentIndexStr = sessionStorage.getItem('currentDriveAssessmentIndex')
      
      console.log('[TestPage] Checking for next assessment:', {
        hasSequence: !!assessmentSequenceStr,
        hasIndex: !!currentIndexStr,
        currentIndexStr,
        assessmentSequenceStr: assessmentSequenceStr ? 'exists' : 'missing'
      })
      
      if (assessmentSequenceStr && currentIndexStr) {
        try {
          const assessmentSequence = JSON.parse(assessmentSequenceStr)
          const currentIndex = parseInt(currentIndexStr, 10)
          const nextIndex = currentIndex + 1
          
          console.log('[TestPage] Assessment sequence check:', {
            sequenceLength: assessmentSequence.length,
            currentIndex,
            nextIndex,
            hasNext: nextIndex < assessmentSequence.length,
            sequence: assessmentSequence.map((a: any) => ({ id: a.id, title: a.title }))
          })
          
          if (nextIndex < assessmentSequence.length) {
            // More assessments remaining - start next one
            toast.success('Assessment submitted! Starting next assessment...', {
              duration: 3000
            })
            
            // Update current index in sessionStorage
            sessionStorage.setItem('currentDriveAssessmentIndex', String(nextIndex))
            
            // Get the next assessment
            const nextAssessment = assessmentSequence[nextIndex]
            
            // Generate questions for the next assessment
            // Split only at the FIRST underscore so multi-word types like "quantitative_ability" are preserved
            const _firstUs1 = nextAssessment.id.indexOf('_')
            const driveId = nextAssessment.id.substring(0, _firstUs1)
            const assessmentType = nextAssessment.id.substring(_firstUs1 + 1)
            const numQuestions = nextAssessment.numQuestions || 1
            const durationMinutes = nextAssessment.duration_minutes || 30
            
            const questionsResponse = await assessmentAPI.generateQuestions(
              driveId,
              assessmentType,
              numQuestions,
              durationMinutes
            )
            
            const generatedQuestions = questionsResponse.data.questions
            
            // Create new session for next assessment
            const nextSessionResponse = await dispatch(createSession({
              assessment_id: nextAssessment.id,
              questions: generatedQuestions,
              duration_minutes: durationMinutes,
              assessment_type: assessmentType,
              candidate_email: user?.email
            }))
            
            if (nextSessionResponse.payload) {
              const nextSession = nextSessionResponse.payload as any
              console.log('[TestPage] Next assessment session created, navigating to instructions:', {
                nextSessionId: nextSession.session_id,
                nextAssessmentId: nextAssessment.id,
                nextIndex,
                updatedIndexInStorage: sessionStorage.getItem('currentDriveAssessmentIndex')
              })
              // Navigate to instructions page for next assessment
              navigate(`/test/${nextSession.session_id}/instructions`, { replace: true })
              return
            } else {
              console.error('[TestPage] Failed to create next session, payload is null')
            }
              } else {
                // All assessments completed
                console.log('[TestPage] All assessments completed, navigating to completion page')
                toast.success('All assessments completed successfully!', {
                  duration: 4000
                })
                // Clear sessionStorage
                const currentDriveId = sessionStorage.getItem('currentDriveId')
                sessionStorage.removeItem('currentDriveAssessmentSequence')
                sessionStorage.removeItem('currentDriveAssessmentIndex')
                sessionStorage.removeItem('currentDriveId')
                sessionStorage.removeItem('assessmentSequenceStarted')
                // Clear the "left" flag if it exists (since all assessments were completed normally)
                if (currentDriveId) {
                  sessionStorage.removeItem(`drive_${currentDriveId}_left`)
                }
                navigate(`/test/${sessionId}/completed`, { replace: true })
                return
              }
        } catch (error) {
          console.error('[TestPage] Error processing next assessment:', error)
          console.error('Full error details:', error)
          toast.error('Error starting next assessment')
          // Fall through to navigate to completed page
        }
      } else {
        console.log('[TestPage] No assessment sequence found in sessionStorage, navigating to completion page')
        console.log('[TestPage] sessionStorage state:', {
          sequence: sessionStorage.getItem('currentDriveAssessmentSequence'),
          index: sessionStorage.getItem('currentDriveAssessmentIndex'),
          driveId: sessionStorage.getItem('currentDriveId')
        })
      }
      
      // No sequence or error - navigate to completed page
      toast.success('Test submitted successfully!')
      navigate(`/test/${sessionId}/completed`, { replace: true })
    } catch (error) {
      console.error('Failed to submit test:', error)
      toast.error('Failed to submit test. Please try again.')
    }
  }

  const handleCodeChange = (code: string) => {
    if (!currentQuestion) return
    
    const responseData = {
      ...currentResponse?.response_data,
      source_code: code,
      language: selectedLanguage,
    }
    
    handleAutoSave(currentQuestion.id, responseData)
  }

  const handleLanguageChange = (newLanguage: string) => {
    setSelectedLanguage(newLanguage)
    
    // Don't auto-save immediately when switching languages
    // Let the CodeEditor handle the code change and then auto-save
  }

  const handleTextChange = (text: string) => {
    if (!currentQuestion) return
    
    const responseData = {
      ...currentResponse?.response_data,
      text_response: text,
    }
    
    handleAutoSave(currentQuestion.id, responseData)
  }

  const handleMCQChange = (selectedOption: any) => {
    if (!currentQuestion || !currentSession || !user) return
    
    // Ensure selectedOption is always a string
    const selectedOptionStr = String(selectedOption || '').trim()
    
    if (!selectedOptionStr) {
      console.warn('[handleMCQChange] Empty selected option, skipping save')
      return
    }
    
    const responseData = {
      ...currentResponse?.response_data,
      selected_option: selectedOptionStr,
    }
    
    dispatch(updateResponse({ 
      questionId: currentQuestion.id, 
      responseData 
    }))
    
    // Save to new assessment responses system
    const candidateId = user.id
    const driveId = resolveDriveId()
    const assessmentId = `${driveId ?? ''}_${currentSession.assessment_type}`
    
    if (driveId !== null) {
      dispatch(saveQuestionResponse({
        assessmentId,
        candidateId,
        driveId,
        questionId: currentQuestion.id.toString(),
        selectedOption: selectedOptionStr
      }))
    }
    
    // Also save to backend (legacy system)
    handleAutoSave(currentQuestion.id, responseData)
  }

  const getQuestionIcon = (questionType: string) => {
    switch (questionType) {
      case 'coding':
        return <CodeBracketIcon className="w-4 h-4" />
      case 'essay':
      case 'case_study':
        return <PencilSquareIcon className="w-4 h-4" />
      default:
        return <DocumentTextIcon className="w-4 h-4" />
    }
  }

  const getQuestionStatus = (questionId: number): 'unanswered' | 'answered' | 'current' => {
    if (questionId === currentQuestion?.id) return 'current'
    if (responses[questionId]?.response_data) return 'answered'
    return 'unanswered'
  }

  // Handle panel resizing
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true)
    e.preventDefault()
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return
    
    const containerWidth = window.innerWidth
    const newLeftWidth = (e.clientX / containerWidth) * 100
    const clampedWidth = Math.min(Math.max(newLeftWidth, 20), 80) // Between 20% and 80%
    setLeftPanelWidth(clampedWidth)
  }

  const handleMouseUp = () => {
    setIsResizing(false)
  }

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    } else {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  // Security check: Ensure only candidates can access test pages
  useEffect(() => {
    if (user && user.role !== 'candidate') {
      toast.error('Access denied. Only candidates can access test pages.')
      navigate('/dashboard')
      return
    }
  }, [user, navigate])

  // Initialize language based on current question
  useEffect(() => {
    if (currentQuestion?.question_type === 'coding') {
      // If question has language restrictions, use the first one
      if (currentQuestion.language_restrictions && currentQuestion.language_restrictions.length > 0) {
        setSelectedLanguage(currentQuestion.language_restrictions[0])
      }
      // If question has starter code, prefer languages that have starter code
      else if (currentQuestion.starter_code) {
        const availableLanguages = Object.keys(currentQuestion.starter_code)
        if (availableLanguages.length > 0) {
          setSelectedLanguage(availableLanguages[0])
        }
      }
    }
  }, [currentQuestion])

  // Finalize timer expiry here, after pending answers have been saved.
  useEffect(() => {
    // Only trigger if timer is running and has actually expired (not just initialized at 0)
    if (remainingTimeSeconds <= 0 && isRunning && !autoSubmitTriggeredRef.current && sessionId && currentSession) {
      autoSubmitTriggeredRef.current = true
      console.log('TestPage: Timer expired, triggering failsafe auto-submit...')
      
      // Auto-submit without showing popup
      const autoSubmit = async () => {
        try {
          // Submit using new assessment responses system (only if we have a valid drive_id)
          const candidateId = user?.id
          const driveId = resolveDriveId()
          const assessmentId = `${driveId ?? ''}_${currentSession.assessment_type}`

          if (driveId !== null && candidateId) {
            // Save all coding question responses that haven't been submitted yet
            const codingQuestions = questions.filter(q => q.question_type === 'coding')
            const savePromises = []
            
            for (const question of codingQuestions) {
              if (submittedCodingQuestions.has(question.id)) {
                continue
              }

              const response = responses[question.id]
              const sourceCode = response?.response_data?.source_code || ''
              
              if (sourceCode && sourceCode.trim()) {
                try {
                  const savePromise = assessmentAPI.submitCodingSolution(assessmentId, {
                    candidate_id: candidateId,
                    drive_id: driveId,
                    question_id: String(question.id),
                    source_code: sourceCode,
                    language: response?.response_data?.language || selectedLanguage || 'python'
                  }).catch(error => {
                    console.error(`Failed to save coding response for question ${question.id}:`, error)
                    return null
                  })
                  savePromises.push(savePromise)
                } catch (error) {
                  console.error(`Error preparing save for question ${question.id}:`, error)
                }
              }
            }
            
            if (savePromises.length > 0) {
              await Promise.allSettled(savePromises)
            }
            
            // Submit the assessment
            await dispatch(submitAssessment({
              assessmentId,
              candidateId,
              driveId,
              submissionReason: 'timer_expired'
            })).unwrap()
          }

          await dispatch(submitSession({ sessionId, reason: 'timer_expired' })).unwrap()
          
          // Completion status is tracked in MySQL via assessment_responses table
          // No need for localStorage - MySQL is the source of truth
          if (currentSession && user?.email) {
            console.log('Auto-submitted assessment - tracked in MySQL for user:', user.email)
          }
          
          dispatch(resetTimer())
          timerInitializedRef.current = false
          
          // Check if there are more assessments in the drive sequence
          const assessmentSequenceStr = sessionStorage.getItem('currentDriveAssessmentSequence')
          const currentIndexStr = sessionStorage.getItem('currentDriveAssessmentIndex')
          
          if (assessmentSequenceStr && currentIndexStr) {
            try {
              const assessmentSequence = JSON.parse(assessmentSequenceStr)
              const currentIndex = parseInt(currentIndexStr, 10)
              const nextIndex = currentIndex + 1
              
              if (nextIndex < assessmentSequence.length) {
                // More assessments remaining - start next one
                toast.success('Assessment submitted! Starting next assessment...', {
                  duration: 3000
                })
                
                sessionStorage.setItem('currentDriveAssessmentIndex', String(nextIndex))
                const nextAssessment = assessmentSequence[nextIndex]
                
                // Split only at the FIRST underscore so multi-word types like "quantitative_ability" are preserved
                const _firstUs2 = nextAssessment.id.indexOf('_')
                const driveId = nextAssessment.id.substring(0, _firstUs2)
                const assessmentType = nextAssessment.id.substring(_firstUs2 + 1)
                const numQuestions = nextAssessment.numQuestions || 1
                const durationMinutes = nextAssessment.duration_minutes || 30
                
                const questionsResponse = await assessmentAPI.generateQuestions(
                  driveId,
                  assessmentType,
                  numQuestions,
                  durationMinutes
                )
                
                const generatedQuestions = questionsResponse.data.questions
                
                const nextSessionResponse = await dispatch(createSession({
                  assessment_id: nextAssessment.id,
                  questions: generatedQuestions,
                  duration_minutes: durationMinutes,
                  assessment_type: assessmentType,
                  candidate_email: user?.email
                }))
                
                if (nextSessionResponse.payload) {
                  const nextSession = nextSessionResponse.payload as any
                  navigate(`/test/${nextSession.session_id}/instructions`, { replace: true })
                  return
                }
              } else {
                // All assessments completed
                toast.success('All assessments completed successfully!', {
                  duration: 4000
                })
                const currentDriveId = sessionStorage.getItem('currentDriveId')
                sessionStorage.removeItem('currentDriveAssessmentSequence')
                sessionStorage.removeItem('currentDriveAssessmentIndex')
                sessionStorage.removeItem('currentDriveId')
                sessionStorage.removeItem('assessmentSequenceStarted')
                // Clear the "left" flag if it exists (since all assessments were completed normally)
                if (currentDriveId) {
                  sessionStorage.removeItem(`drive_${currentDriveId}_left`)
                }
                navigate(`/test/${sessionId}/completed`, { replace: true })
                return
              }
            } catch (error) {
              console.error('Error processing next assessment:', error)
            }
          }
          
          toast.success('Test submitted successfully!')
          navigate(`/test/${sessionId}/completed`, { replace: true })
        } catch (error) {
          console.error('Failed to auto-submit test:', error)
          autoSubmitTriggeredRef.current = false
          toast.error('Submission is not confirmed. Please retry submitting the test.')
        }
      }
      
      autoSubmit()
    }
  }, [remainingTimeSeconds, isRunning, sessionId, currentSession, dispatch, navigate])

  if (!isInitialized || !currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed Header with Timer, Question Number, and Submit */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-600">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          {!isConnected() && (
            <ExclamationTriangleIcon className="w-4 h-4 text-warning-500" title="Connection issues" />
          )}
        </div>
        
        <div className="flex items-center space-x-3">
          <Timer sessionId={sessionId!} size="small" />
          <button
            onClick={handleSubmitTest}
            disabled={isSubmitting}
            className="btn btn-danger btn-sm"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Test'}
          </button>
        </div>
      </div>

      {/* Main Content Area with top padding to account for fixed header */}
      <div className="pt-16 flex">
        {/* Floating Sidebar Toggle Button */}
        {!isSidebarOpen && (
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="fixed left-2 top-20 z-40 w-8 h-8 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
            title="Show Questions"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        )}

        {/* Collapsible Sidebar - Question Navigation */}
        {isSidebarOpen && (
          <div className="w-64 bg-white flex flex-col">
            {/* Sidebar Header with Close Button */}
            <div className="p-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Questions</h3>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                title="Hide Questions"
              >
                <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            
            {/* Question Navigation */}
            <div className="flex-1 py-4 overflow-y-auto">
              <div className="px-4 space-y-2">
                {questions.map((question, index) => {
                  const status = getQuestionStatus(question.id)
                  return (
                    <button
                      key={question.id}
                      onClick={() => handleQuestionChange(index)}
                      className={clsx(
                        'w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors',
                        status === 'current' && 'bg-primary-100 border border-primary-200 text-primary-700',
                        status === 'answered' && 'bg-success-50 border border-success-200 text-success-700',
                        status === 'unanswered' && 'bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100'
                      )}
                    >
                      <div className="flex-shrink-0">
                        {getQuestionIcon(question.question_type)}
                      </div>
                      <span className="font-medium">Question {index + 1}</span>
                      {status === 'answered' && (
                        <CheckIcon className="w-4 h-4 text-success-600 ml-auto" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">

        {/* Question Content Area */}
        <div className="flex-1 flex">
          {/* Question Panel */}
          <div 
            className="flex flex-col"
            style={{ width: currentQuestion.question_type === 'coding' ? `${leftPanelWidth}%` : '100%' }}
          >

          {/* Question content */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="mb-6">
              {currentQuestion.title && (
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {currentQuestion.title}
                </h2>
              )}
              
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ 
                  __html: currentQuestion.question_type === 'coding' && currentQuestion.question_data?.problem_statement 
                    ? currentQuestion.question_data.problem_statement 
                    : currentQuestion.description 
                }}
              />
              
              {/* Display question image if present */}
              {(currentQuestion.question_data?.image || currentQuestion.image) && (
                <div className="mt-4">
                  <img
                    src={currentQuestion.question_data?.image || currentQuestion.image}
                    alt="Question diagram or figure"
                    className="max-w-full h-auto rounded-lg border border-gray-200 shadow-sm"
                    style={{ maxHeight: '400px' }}
                  />
                </div>
              )}
              
              {/* Display example test cases for coding questions */}
              {currentQuestion.question_type === 'coding' && currentQuestion.question_data?.example_test_cases && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Example Test Cases</h3>
                  <div className="space-y-4">
                    {currentQuestion.question_data.example_test_cases.map((testCase: any, index: number) => (
                      <div key={testCase.id || index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="font-medium text-gray-700 mb-3">Example {index + 1}</div>
                        
                        {/* Inputs */}
                        <div className="mb-3">
                          <div className="text-sm font-medium text-gray-600 mb-1">Input:</div>
                          <div className="bg-white rounded border border-gray-200 p-3 font-mono text-sm">
                            {testCase.inputs && testCase.inputs.map((input: any, idx: number) => (
                              <div key={idx} className="mb-1">
                                <span className="text-blue-600">{input.name}</span> = {JSON.stringify(input.value)}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Output */}
                        <div className="mb-3">
                          <div className="text-sm font-medium text-gray-600 mb-1">Expected Output:</div>
                          <div className="bg-white rounded border border-gray-200 p-3 font-mono text-sm">
                            {JSON.stringify(testCase.output?.value)}
                          </div>
                        </div>
                        
                        {/* Explanation */}
                        {testCase.explanation && (
                          <div>
                            <div className="text-sm font-medium text-gray-600 mb-1">Explanation:</div>
                            <div className="text-sm text-gray-700 bg-blue-50 border border-blue-200 rounded p-3">
                              {testCase.explanation}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Hidden test cases notice */}
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium">Note:</span> Your solution will also be tested against hidden test cases that are not shown here. Make sure your solution handles edge cases and follows the constraints.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Display constraints for coding questions */}
              {currentQuestion.question_type === 'coding' && currentQuestion.question_data?.constraints && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Constraints</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    {currentQuestion.question_data.constraints.items?.map((constraint: string, index: number) => (
                      <li key={index} className="font-mono">{constraint}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Display hints for coding questions */}
              {currentQuestion.question_type === 'coding' && currentQuestion.question_data?.hints && currentQuestion.question_data.hints.length > 0 && (
                <div className="mt-6">
                  <details className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600">
                      💡 Hints (Click to reveal)
                    </summary>
                    <div className="mt-3 space-y-2">
                      {currentQuestion.question_data.hints.map((hint: string, index: number) => (
                        <div key={index} className="text-sm text-gray-700 bg-white rounded p-3 border border-gray-200">
                          <span className="font-medium text-blue-600">Hint {index + 1}:</span> {hint}
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </div>

            {/* Non-coding answer areas */}
            {(['multiple_choice', 'aptitude', 'fundamentals', 'cloud_fundamentals', 'presales_fundamentals', 'english_communication', 'ai_fundamental', 'quantitative_ability', 'logical_reasoning', 'verbal_ability'].includes(currentQuestion.question_type)) && (
              <div className="space-y-3">
                {(() => {
                  // Handle both object and array formats for options
                  let optionsArray: Array<{id: string, text: string}> = []
                  if (currentQuestion.options) {
                    if (Array.isArray(currentQuestion.options)) {
                      // Already in array format
                      optionsArray = currentQuestion.options.map((opt: any, idx: number) => {
                        // Handle both {id, text} format and string format
                        if (typeof opt === 'string') {
                          return { id: opt, text: opt }
                        } else if (opt && typeof opt === 'object') {
                          // Ensure we extract id and text properly
                          const optId = opt.id !== undefined ? String(opt.id) : (opt.key !== undefined ? String(opt.key) : String(idx))
                          const optText = opt.text !== undefined ? String(opt.text) : (opt.value !== undefined ? String(opt.value) : '')
                          return { id: optId, text: optText }
                        }
                        return { id: String(idx), text: String(opt) }
                      })
                    } else if (typeof currentQuestion.options === 'object' && currentQuestion.options !== null) {
                      // Convert object format {A: "text", B: "text"} to array format [{id: "A", text: "text"}, ...]
                      optionsArray = Object.entries(currentQuestion.options).map(([key, value]) => ({
                        id: String(key),
                        text: typeof value === 'string' ? value : String(value)
                      }))
                    }
                  }
                  
                  // Debug log to see what we're working with
                  if (optionsArray.length > 0 && (optionsArray[0].id === '[object Object]' || optionsArray[0].text === '[object Object]')) {
                    console.error('[TestPage] Options parsing issue:', {
                      rawOptions: currentQuestion.options,
                      parsedOptions: optionsArray
                    })
                  }
                  
                  return optionsArray.map((option, index) => {
                    // Ensure optionId is always a string to prevent "[object Object]" display
                    // IMPORTANT: Use the option's id property (A, B, C, D) NOT the array index (0, 1, 2, 3)
                    // This matches aptitude behavior and ensures correct answer matching
                    // Map numeric IDs (0, 1, 2, 3) to letters (A, B, C, D) to match aptitude
                    const letterMap = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
                    let optionId: string = ''
                    if (option) {
                      if (typeof option === 'string') {
                        optionId = option
                      } else if (typeof option === 'object') {
                        const opt = option as any
                        // Try to get id from various possible properties - prioritize 'id' field
                        const idValue = opt.id || opt.key || opt.value
                        if (idValue !== undefined && idValue !== null) {
                          if (typeof idValue === 'string') {
                            optionId = idValue
                          } else if (typeof idValue === 'number') {
                            // Convert numeric ID (0, 1, 2, 3) to letter (A, B, C, D) to match aptitude
                            if (idValue >= 0 && idValue < letterMap.length) {
                              optionId = letterMap[idValue]
                            } else {
                              optionId = String(idValue)
                            }
                          } else {
                            // If it's an object, use letter mapping
                            optionId = letterMap[index] || String(index)
                            console.warn('[TestPage] Option id is an object, using letter mapping:', { option, index, optionId })
                          }
                        } else {
                          // If no id found, use letter mapping (A, B, C, D) instead of index
                          optionId = letterMap[index] || String(index)
                          console.warn('[TestPage] Option missing id field, using letter mapping:', { option, index, optionId })
                        }
                      }
                    } else {
                      // Fallback - use letter mapping
                      optionId = letterMap[index] || String(index)
                    }
                    
                    // Final safeguard: if optionId is still a number string (0, 1, 2, 3), convert to letter
                    try {
                      const numId = parseInt(optionId)
                      if (!isNaN(numId) && numId >= 0 && numId < letterMap.length) {
                        optionId = letterMap[numId]
                      }
                    } catch (e) {
                      // Not a number, keep as is
                    }
                    
                    // Ensure optionText is always a string
                    let optionText: string = ''
                    if (option) {
                      if (typeof option === 'string') {
                        optionText = option
                      } else if (typeof option === 'object') {
                        const opt = option as any
                        // Try to get text from various possible properties
                        const textValue = opt.text || opt.value || opt.label
                        if (textValue !== undefined && textValue !== null) {
                          if (typeof textValue === 'string') {
                            optionText = textValue
                          } else if (typeof textValue === 'number') {
                            optionText = String(textValue)
                          } else {
                            // If it's an object, we can't extract meaningful text
                            optionText = ''
                          }
                        }
                      }
                    }
                    
                    // Only mark as selected if there's an actual saved response (not default/null)
                    const selectedOption = currentResponse?.response_data?.selected_option
                    const isSelected = selectedOption !== undefined && 
                                      selectedOption !== null && 
                                      selectedOption !== '' &&
                                      String(selectedOption).trim() === optionId.trim()
                    
                    return (
                      <label key={index} htmlFor={`option-${currentQuestion.id}-${index}`} className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors">
                        <input
                          id={`option-${currentQuestion.id}-${index}`}
                          type="radio"
                          name={`question-${currentQuestion.id}`}
                          value={optionId}
                          checked={isSelected}
                          onChange={() => handleMCQChange(optionId)}
                          className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-gray-900">
                          <span className="font-medium">{optionId}.</span> {optionText}
                        </span>
                      </label>
                    )
                  })
                })()}
              </div>
            )}

            {/* AI/ML Assignment - Link Submission */}
            {(currentQuestion.question_type as string) === 'aiml_assignment' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">📋 Assignment Submission</h4>
                  <p className="text-sm text-blue-700">
                    Submit a link to your completed assignment (e.g., GitHub repository, Google Drive, Dropbox, etc.)
                  </p>
                </div>
                <div>
                  <label htmlFor="assignment-link" className="block text-sm font-medium text-gray-700 mb-2">
                    Assignment Link *
                  </label>
                  <input
                    id="assignment-link"
                    type="url"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    placeholder="https://github.com/username/project or https://drive.google.com/..."
                    value={currentResponse?.response_data?.assignment_link || ''}
                    onChange={(e) => {
                      const assignmentLink = e.target.value
                      
                      // Update local state immediately to make input editable
                      dispatch(updateResponse({
                        questionId: currentQuestion.id,
                        responseData: {
                          ...currentResponse?.response_data,
                          assignment_link: assignmentLink
                        }
                      }))
                      
                      // Auto-save to backend after 1 second
                      if (autoSaveTimeoutRef.current) {
                        clearTimeout(autoSaveTimeoutRef.current)
                      }
                      
                      autoSaveTimeoutRef.current = setTimeout(async () => {
                        if (sessionId && currentQuestion.id && assignmentLink.trim()) {
                          try {
                            const driveId = resolveDriveId()
                            if (driveId) {
                              const assessmentId = `${driveId}_${currentSession?.assessment_type || 'aiml_assignment'}`
                              
                              console.log('[AI/ML Assignment] Submitting:', {
                                driveId,
                                assessmentId,
                                assessment_type: currentSession?.assessment_type,
                                assignmentLink
                              })
                              
                              // Submit assignment link to backend
                              const token = localStorage.getItem('access_token') || localStorage.getItem('token')
                              const response = await fetch(`/api/v1/drives/${driveId}/assignments/submit`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                  assessment_id: assessmentId,
                                  assignment_link: assignmentLink
                                })
                              })
                              
                              console.log('Assignment link saved:', assignmentLink)
                              
                              if (response.ok) {
                                console.log('Assignment link submission successful')
                              } else {
                                const error = await response.text()
                                console.error('Assignment link submission failed:', error)
                              }
                            }
                          } catch (error) {
                            console.error('Error saving assignment link:', error)
                          }
                        }
                      }, 1000)
                    }}
                  />
                  {currentResponse?.response_data?.assignment_link && (
                    <p className="mt-2 text-sm text-green-600">
                      ✓ Link saved: {currentResponse.response_data.assignment_link}
                    </p>
                  )}
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">💡 Supported Platforms:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• GitHub / GitLab / Bitbucket</li>
                    <li>• Google Drive / Dropbox / OneDrive</li>
                    <li>• Colab Notebooks / Kaggle Kernels</li>
                    <li>• Any publicly accessible URL</li>
                  </ul>
                  <p className="text-xs text-gray-500 mt-3">
                    ⚠️ Make sure the link is accessible and permissions are set correctly
                  </p>
                </div>
              </div>
            )}

            {!['coding', 'multiple_choice', 'aptitude', 'fundamentals', 'cloud_fundamentals', 'presales_fundamentals', 'english_communication', 'ai_fundamental', 'aiml_assignment', 'quantitative_ability', 'logical_reasoning', 'verbal_ability'].includes(currentQuestion.question_type) && (
              <div>
                <textarea
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder={`Write your ${currentQuestion.question_type} response here...`}
                  value={currentResponse?.response_data?.text_response || ''}
                  onChange={(e) => handleTextChange(e.target.value)}
                />
                {currentQuestion.word_limit && (
                  <div className="mt-2 text-sm text-gray-500">
                    Word limit: {currentQuestion.word_limit}
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-6 pt-4">
              <button
                onClick={handlePrevQuestion}
                disabled={currentQuestionIndex === 0}
                className="btn btn-outline flex items-center space-x-2"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                <span>Previous</span>
              </button>
              
              {/* Only show Next button if not on the last question */}
              {currentQuestionIndex < questions.length - 1 && (
                <button
                  onClick={handleNextQuestion}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <span>Next</span>
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Resize Handle */}
        {currentQuestion.question_type === 'coding' && (
          <div
            className="w-1 hover:bg-gray-300 cursor-col-resize flex items-center justify-center group"
            onMouseDown={handleMouseDown}
          >
            <div className="w-0.5 h-8 bg-gray-400 group-hover:bg-gray-500 rounded-full"></div>
          </div>
        )}

        {/* Right Panel - Code Editor (only for coding questions) */}
        {currentQuestion.question_type === 'coding' && (
          <div 
            className="flex flex-col"
            style={{ width: `${100 - leftPanelWidth}%` }}
          >
            <CodeEditor
              questionId={currentQuestion.id}
              sessionId={sessionId!}
              language={selectedLanguage}
              onLanguageChange={handleLanguageChange}
              starterCode={currentQuestion.starter_code}
              testCases={currentQuestion.test_cases}
              onCodeChange={handleCodeChange}
              assessmentId={resolveDriveId() !== null && currentSession ? `${resolveDriveId()!}_${currentSession.assessment_type}` : undefined}
              candidateId={user?.id}
              driveId={resolveDriveId() ?? undefined}
              onCodeSubmitted={(questionId) => {
                setSubmittedCodingQuestions(prev => new Set(prev).add(questionId))
                console.log(`[TestPage] Code submitted for question ${questionId}`)
              }}
            />
          </div>
        )}
        </div>
      </div>
      </div>
      
      {/* Warning Modal for Back Navigation */}
      <WarningModalComponent />
      
      {/* Assessment Sequence Navigation Warning Modal */}
      {showSequenceWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Navigation Warning</h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Navigation Back from test is not allowed, complete all Assessment in one go. Sure want to leave the test?
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={async () => {
                  try {
                    console.log('[TestPage] Leave Test clicked - submitting current assessment and all remaining assessments')
                    
                    const assessmentSequenceStr = sessionStorage.getItem('currentDriveAssessmentSequence')
                    const currentIndexStr = sessionStorage.getItem('currentDriveAssessmentIndex')
                    const driveId = resolveDriveId()
                    const candidateId = user?.id
                    
                    // Submit current assessment before leaving
                    if (sessionId && currentSession && user && driveId !== null && candidateId) {
                      const currentAssessmentId = `${driveId}_${currentSession.assessment_type}`

                      // Save all coding question responses that haven't been submitted yet
                      const codingQuestions = questions.filter(q => q.question_type === 'coding')
                      const savePromises = []
                      
                      for (const question of codingQuestions) {
                        if (submittedCodingQuestions.has(question.id)) {
                          continue
                        }

                        const response = responses[question.id]
                        const sourceCode = response?.response_data?.source_code || ''
                        
                        if (sourceCode && sourceCode.trim()) {
                          try {
                            const savePromise = assessmentAPI.submitCodingSolution(currentAssessmentId, {
                              candidate_id: candidateId,
                              drive_id: driveId,
                              question_id: String(question.id),
                              source_code: sourceCode,
                              language: response?.response_data?.language || selectedLanguage || 'python'
                            }).catch(error => {
                              console.error(`Failed to save coding response for question ${question.id}:`, error)
                              return null
                            })
                            savePromises.push(savePromise)
                          } catch (error) {
                            console.error(`Error preparing save for question ${question.id}:`, error)
                          }
                        }
                      }
                      
                      if (savePromises.length > 0) {
                        await Promise.allSettled(savePromises)
                      }
                      
                      // Submit the current assessment
                      await dispatch(submitAssessment({
                        assessmentId: currentAssessmentId,
                        candidateId,
                        driveId
                      })).unwrap()

                      // Submit session
                      await dispatch(submitSession(sessionId)).unwrap()
                    }
                    
                    // Submit all remaining assessments in the sequence (mark as completed with 0 score)
                    if (assessmentSequenceStr && currentIndexStr && driveId !== null && candidateId) {
                      try {
                        const assessmentSequence = JSON.parse(assessmentSequenceStr)
                        const currentIndex = parseInt(currentIndexStr, 10)
                        
                        console.log('[TestPage] Submitting remaining assessments:', {
                          currentIndex,
                          totalAssessments: assessmentSequence.length,
                          remaining: assessmentSequence.length - currentIndex - 1
                        })
                        
                        // Submit all remaining assessments (after current one)
                        for (let i = currentIndex + 1; i < assessmentSequence.length; i++) {
                          const remainingAssessment = assessmentSequence[i]
                          const remainingAssessmentId = remainingAssessment.id
                          
                          try {
                            // Mark remaining assessment as submitted (with 0 responses)
                            // This ensures they show as completed but with 0 score
                            await dispatch(submitAssessment({
                              assessmentId: remainingAssessmentId,
                              candidateId,
                              driveId
                            })).unwrap()
                            console.log(`[TestPage] Marked assessment ${remainingAssessmentId} as completed (left test)`)
                          } catch (error) {
                            console.error(`[TestPage] Error submitting remaining assessment ${remainingAssessmentId}:`, error)
                            // Continue with other assessments even if one fails
                          }
                        }
                      } catch (error) {
                        console.error('[TestPage] Error processing remaining assessments:', error)
                      }
                    }
                    
                    // Mark drive as left (so Start Test button is disabled)
                    if (driveId !== null) {
                      sessionStorage.setItem(`drive_${driveId}_left`, 'true')
                      console.log(`[TestPage] Marked drive ${driveId} as left`)
                    }
                    
                    // Clear assessment sequence data
                    sessionStorage.removeItem('currentDriveAssessmentSequence')
                    sessionStorage.removeItem('currentDriveAssessmentIndex')
                    sessionStorage.removeItem('currentDriveId')
                    sessionStorage.removeItem('assessmentSequenceStarted')
                    setShowSequenceWarning(false)
                    
                    toast.success('All assessments have been submitted. You have left the test.')
                    navigate('/dashboard', { replace: true })
                  } catch (error) {
                    console.error('[TestPage] Error submitting assessments before leaving:', error)
                    toast.error('Error submitting assessments. Please try again.')
                    // Stay here if finalization failed; leaving must never look
                    // successful before the backend confirms submission.
                  }
                }}
                className="flex-1 px-4 py-2 border border-red-300 rounded-md text-red-700 bg-red-50 hover:bg-red-100 transition-colors font-medium"
              >
                Leave Test
              </button>
              <button
                onClick={() => {
                  setShowSequenceWarning(false)
                  window.history.pushState(null, '', location.pathname)
                }}
                className="flex-1 px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors font-medium"
              >
                Continue Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TestPage
